import React, { useState } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle } from 'lucide-react';
import BrandLogo from '../components/branding/BrandLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pwd.length < 8) return setError('Password must be at least 8 characters.');
    if (pwd !== confirm) return setError('Passwords do not match.');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, newPassword: pwd });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not reset password. The link may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="w-full max-w-md space-y-8">
        <BrandLogo variant="full" imgClassName="h-16 w-auto mx-auto" />
        {done ? (
          <div className="text-center space-y-4" data-testid="reset-success">
            <CheckCircle className="w-12 h-12 mx-auto text-green-600" />
            <h1 className="text-2xl font-bold text-foreground">Password updated</h1>
            <p className="text-sm text-muted-foreground">Redirecting you to sign in…</p>
          </div>
        ) : !token ? (
          <div className="text-center space-y-3" data-testid="reset-no-token">
            <h1 className="text-2xl font-bold text-foreground">Invalid reset link</h1>
            <p className="text-sm text-muted-foreground">This link is missing its token. Please request a new one.</p>
            <a href="/forgot-password" className="text-primary hover:underline text-sm">Request a new link</a>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="reset-password-form">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-foreground">Set a new password</h1>
              <p className="text-sm text-muted-foreground mt-2">Choose a strong password you haven't used before.</p>
            </div>
            {error && <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm" data-testid="reset-error">{error}</div>}
            <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} required placeholder="New password"
              className="h-12 w-full px-4 bg-background border border-border rounded-md outline-none focus:ring-2 focus:ring-primary/30" data-testid="reset-password-input" />
            <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required placeholder="Confirm new password"
              className="h-12 w-full px-4 bg-background border border-border rounded-md outline-none focus:ring-2 focus:ring-primary/30" data-testid="reset-confirm-input" />
            <button type="submit" disabled={loading}
              className="h-12 w-full bg-primary text-primary-foreground font-bold rounded-md disabled:opacity-50 flex items-center justify-center gap-2" data-testid="reset-submit-button">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Update password'}
            </button>
            <a href="/login" className="block text-center text-sm text-muted-foreground hover:text-foreground">Back to sign in</a>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
